import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Route, Switch, useHistory, useLocation, useRouteMatch, Redirect } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { api } from '../services/api';
import AccountOverview from './AccountOverview';
import AccountDetails from './AccountDetails';
import OrganizationDetails from './OrganizationDetails';
import UserManagement from './UserManagement';
import AuditTrail from './AuditTrail';
import Invites from './Invites';
import InviteHistory from './InviteHistory';
import ApiKeys from './ApiKeys';
import ApiKeyUsage from './ApiKeyUsage';
import EmailUpdate from './EmailUpdate';
import Subscription from './Subscription';
import SubscriptionCheckout from './SubscriptionCheckout';
import Support from './Support';
import SupportEmailTemplates from './SupportEmailTemplates';
import OpsLoginAttempts from './OpsLoginAttempts';
import OpsAuthThrottles from './OpsAuthThrottles';
import OpsOrgSelect from './OpsOrgSelect';
import OpsOrgShell from './OpsOrgShell';
import OpsUserMetrics from './OpsUserMetrics';
import OpsAuditHistory from './OpsAuditHistory';
import OpsTaxExemptionRequests from './OpsTaxExemptionRequests';
import OpsSupportTicketCleanup from './OpsSupportTicketCleanup';
import OpsSubscriptionCatalog from './OpsSubscriptionCatalog';
import OpsDataSetManagement from './OpsDataSetManagement';
import OpsMaintenance from './OpsMaintenance';
import PlatformMessagesBanner from './PlatformMessagesBanner';
import '../styles/Dashboard.css';
import { getHostKind, getOrigins } from '../utils/host';
import finbaseLogo from '../assets/logos/finbase-logo.svg';

const normalizeOrgUsers = (rawUsers = []) => {
  const map = new Map();
  rawUsers.forEach((user) => {
    if (!user || !user.email) return;
    const key = user.email;
    const existing = map.get(key) || {
      id: user.id,
      email: user.email,
      full_name: user.full_name,
      is_internal: user.is_internal,
      last_login_at: user.last_login_at,
      roles: [],
    };
    if (!existing.id && user.id) existing.id = user.id;
    if (user.full_name && !existing.full_name) existing.full_name = user.full_name;
    if (typeof user.is_internal === 'boolean') existing.is_internal = user.is_internal;
    if (user.last_login_at) {
      const next = new Date(user.last_login_at);
      const current = existing.last_login_at ? new Date(existing.last_login_at) : null;
      if (!current || (Number.isFinite(next.getTime()) && next > current)) {
        existing.last_login_at = user.last_login_at;
      }
    }
    if (Array.isArray(user.roles)) {
      user.roles.forEach((role) => {
        if (role && !existing.roles.includes(role)) existing.roles.push(role);
      });
    } else if (user.roles && !existing.roles.includes(user.roles)) {
      existing.roles.push(user.roles);
    }
    map.set(key, existing);
  });
  return Array.from(map.values());
};

const Account = () => {
  const { token, logout } = useContext(AuthContext);
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [opsAccessDenied, setOpsAccessDenied] = useState(false);
  const [taxExemptionPendingCount, setTaxExemptionPendingCount] = useState(0);
  const [supportIdentityCleanupPendingCount, setSupportIdentityCleanupPendingCount] = useState(0);
  const [opsMaintenanceStatuses, setOpsMaintenanceStatuses] = useState([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const blockAuthRedirectRef = useRef(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = window.localStorage.getItem('finbase.sidebarCollapsed');
    return stored === 'true';
  });
  const history = useHistory();
  const location = useLocation();
  const { path, url } = useRouteMatch();
  const opsBasePath = '/admin';
  const hostKind = getHostKind();
  const isOpsHost = hostKind === 'ops';
  const { idOrigin } = getOrigins();

  const fetchAccount = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/acct-mgmt/v1/account');
      setAccountData(response.data);
    } catch (err) {
      console.error('Failed to fetch account data:', err);
      const message = err.response?.data?.error || 'Failed to load account data';
      setError(message);
      if (isOpsHost && err.response?.status === 403) {
        blockAuthRedirectRef.current = true;
        logout();
        setOpsAccessDenied(true);
        return;
      }
      logout();
      history.push('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setAccountData(null);
      if (!blockAuthRedirectRef.current) {
        history.push('/login');
      }
      return;
    }
    fetchAccount();
  }, [token, history]);

  const orgUsers = useMemo(() => normalizeOrgUsers(accountData?.org_users || []), [accountData]);
  const isOrg = accountData?.org_type === 'type-org';
  const roles = accountData?.roles || [];
  const canManageAccount = roles.includes('role-acct-mgmt');
  const canManageKeys = roles.includes('role-key-mgmt');
  const isInternal = accountData?.is_internal && accountData?.org_internal;
  const canSupport = roles.includes('role-support') && isInternal;
  const canAssignSupport = Boolean(accountData?.org_internal);
  const canAssignInternal = Boolean(accountData?.org_internal && accountData?.is_internal);
  const isSupportOnly = canSupport && !canManageAccount && !canManageKeys;
  const showSupportMenu = isOpsHost && canSupport;
  const allowOpsRoutes = isOpsHost && (loading || showSupportMenu);
  const allowAccountMenus = !isOpsHost;
  const serviceApiUrl = accountData?.service_api_url;
  const docsUrl = accountData?.docs_url;
  const currentUserEmail = accountData?.email;
  const opsMaintenanceSummary = opsMaintenanceStatuses
    .map((item) => {
      if (item.scope === 'service_api') return 'Service API';
      if (item.scope === 'account_portal') return 'Account Portal';
      return item.scope;
    })
    .join(', ');
  const apiPermissions = accountData
    ? {
        api_rate_limit: accountData.api_rate_limit,
        api_src_limit: accountData.api_src_limit,
        api_src_derived_flag: accountData.api_src_derived_flag,
        api_hist_limit: accountData.api_hist_limit,
        api_requests_current: accountData.api_requests_current,
      }
    : null;

  useEffect(() => {
    if (!isOpsHost || !accountData) return;
    if (!canSupport) {
      blockAuthRedirectRef.current = true;
      logout();
      setOpsAccessDenied(true);
    }
  }, [accountData, canSupport, isOpsHost, logout]);

  useEffect(() => {
    if (!isOpsHost || !canSupport) {
      setTaxExemptionPendingCount(0);
      return;
    }
    let cancelled = false;
    const loadPendingCount = async () => {
      try {
        const response = await api.get('/acct-mgmt/v1/support/tax-exemption-requests/pending-count');
        if (!cancelled) {
          setTaxExemptionPendingCount(Number(response.data?.pending_count || 0));
        }
      } catch (err) {
        if (!cancelled) {
          setTaxExemptionPendingCount(0);
        }
      }
    };
    loadPendingCount();
    return () => {
      cancelled = true;
    };
  }, [isOpsHost, canSupport, accountData]);

  useEffect(() => {
    if (!isOpsHost || !canSupport) return undefined;
    const handlePendingCountRefresh = async () => {
      try {
        const response = await api.get('/acct-mgmt/v1/support/tax-exemption-requests/pending-count');
        setTaxExemptionPendingCount(Number(response.data?.pending_count || 0));
      } catch (err) {
        // keep current badge count if refresh fails
      }
    };
    window.addEventListener('finbase:tax-exemption-pending-count-refresh', handlePendingCountRefresh);
    return () => {
      window.removeEventListener('finbase:tax-exemption-pending-count-refresh', handlePendingCountRefresh);
    };
  }, [isOpsHost, canSupport]);

  useEffect(() => {
    if (!isOpsHost || !canSupport) {
      setSupportIdentityCleanupPendingCount(0);
      return undefined;
    }
    const handlePendingCountRefresh = async () => {
      try {
        const response = await api.get('/acct-mgmt/v1/support/identity-cleanup-tasks/pending-count');
        setSupportIdentityCleanupPendingCount(Number(response.data?.pending_count || 0));
      } catch (err) {
        setSupportIdentityCleanupPendingCount(0);
      }
    };
    handlePendingCountRefresh();
    window.addEventListener('finbase:support-identity-cleanup-count-refresh', handlePendingCountRefresh);
    return () => {
      window.removeEventListener('finbase:support-identity-cleanup-count-refresh', handlePendingCountRefresh);
    };
  }, [isOpsHost, canSupport, accountData]);

  useEffect(() => {
    if (!isOpsHost || !canSupport) {
      setOpsMaintenanceStatuses([]);
      return undefined;
    }
    let cancelled = false;
    const loadMaintenanceStatuses = async () => {
      try {
        const response = await api.get('/acct-mgmt/v1/support/maintenance-status');
        if (!cancelled) {
          setOpsMaintenanceStatuses((response.data?.statuses || []).filter((item) => item.active_flag));
        }
      } catch (err) {
        if (!cancelled) setOpsMaintenanceStatuses([]);
      }
    };
    loadMaintenanceStatuses();
    const intervalId = window.setInterval(loadMaintenanceStatuses, 60000);
    window.addEventListener('finbase:maintenance-status-refresh', loadMaintenanceStatuses);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('finbase:maintenance-status-refresh', loadMaintenanceStatuses);
    };
  }, [isOpsHost, canSupport, accountData]);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem('finbase.sidebarCollapsed', String(next));
      return next;
    });
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileNavOpen(false);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileNavOpen]);

  const renderSidebarNav = ({ onNavigate } = {}) => (
    <nav className="sidebar-nav" aria-label={`${isOpsHost ? 'Admin' : 'Account'} navigation`}>
      {allowAccountMenus && (
        <>
          <NavLink to={`${url}/overview`} activeClassName="active" title="Overview" onClick={onNavigate}>
            <i className="fas fa-th-large sidebar-icon" aria-hidden="true"></i>
            <span className="sidebar-label">Overview</span>
          </NavLink>
          <NavLink to={`${url}/details`} activeClassName="active" title="Account Details" onClick={onNavigate}>
            <i className="fas fa-id-card sidebar-icon" aria-hidden="true"></i>
            <span className="sidebar-label">Account Details</span>
          </NavLink>
        </>
      )}
      {allowAccountMenus && !isSupportOnly && (
        <NavLink to={`${url}/organization`} activeClassName="active" title="Organization" onClick={onNavigate}>
          <i className="fas fa-building sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Organization</span>
        </NavLink>
      )}
      {allowAccountMenus && isOrg && canManageAccount && !isSupportOnly && (
        <NavLink to={`${url}/users`} activeClassName="active" title="Users" onClick={onNavigate}>
          <i className="fas fa-users sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Users</span>
        </NavLink>
      )}
      {allowAccountMenus && isOrg && canManageAccount && !isSupportOnly && (
        <NavLink to={`${url}/audit`} activeClassName="active" title="Audit History" onClick={onNavigate}>
          <i className="fas fa-clipboard-list sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Audit History</span>
        </NavLink>
      )}
      {allowAccountMenus && isOrg && canManageAccount && !isSupportOnly && (
        <NavLink to={`${url}/invites`} activeClassName="active" title="Invites" onClick={onNavigate}>
          <i className="fas fa-envelope-open-text sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Invites</span>
        </NavLink>
      )}
      {allowAccountMenus && isOrg && canManageAccount && !isSupportOnly && (
        <NavLink to={`${url}/invites/history`} activeClassName="active" className="sidebar-subitem" title="Invite History" onClick={onNavigate}>
          <i className="fas fa-history sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Invite History</span>
        </NavLink>
      )}
      {allowAccountMenus && !isSupportOnly && (
        <>
          <NavLink to={`${url}/api-keys`} activeClassName="active" title="API Keys" onClick={onNavigate}>
            <i className="fas fa-key sidebar-icon" aria-hidden="true"></i>
            <span className="sidebar-label">API Keys</span>
          </NavLink>
          <NavLink to={`${url}/api-keys/usage`} activeClassName="active" className="sidebar-subitem" title="Usage" onClick={onNavigate}>
            <i className="fas fa-chart-line sidebar-icon" aria-hidden="true"></i>
            <span className="sidebar-label">Usage</span>
          </NavLink>
        </>
      )}
      {allowAccountMenus && canManageAccount && !isSupportOnly && (
        <NavLink to={`${url}/subscription`} activeClassName="active" title="Subscription" onClick={onNavigate}>
          <i className="fas fa-credit-card sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Subscription</span>
        </NavLink>
      )}
      {allowAccountMenus && !isOrg && !isSupportOnly && (
        <NavLink to={`${url}/email`} activeClassName="active" title="Email Update" onClick={onNavigate}>
          <i className="fas fa-at sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Email Update</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={opsBasePath} activeClassName="active" title="Organization Support" onClick={onNavigate}>
          <i className="fas fa-sitemap sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Client Support</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/ops-dashboard`} activeClassName="active" title="Dashboard" onClick={onNavigate}>
          <i className="fas fa-chart-pie sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Dashboard</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/login-attempts`} activeClassName="active" title="Login Attempts" onClick={onNavigate}>
          <i className="fas fa-user-shield sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Login Attempts</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/auth-throttle`} activeClassName="active" title="Auth Throttle" onClick={onNavigate}>
          <i className="fas fa-lock sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Auth Throttle</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/tax-exemption-requests`} activeClassName="active" title="Tax Exemption Requests" onClick={onNavigate}>
          <i className="fas fa-receipt sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">
            Tax Exemptions
            {taxExemptionPendingCount > 0 && (
              <span className="badge bg-danger ms-2">{taxExemptionPendingCount}</span>
            )}
          </span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/support-portal-identity-cleanup`} activeClassName="active" title="Support Portal Identity Cleanup" onClick={onNavigate}>
          <i className="fas fa-broom sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">
            Identity Cleanup
            {supportIdentityCleanupPendingCount > 0 && (
              <span className="badge bg-danger ms-2">{supportIdentityCleanupPendingCount}</span>
            )}
          </span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/subscription-catalog`} activeClassName="active" title="Subscription Catalog" onClick={onNavigate}>
          <i className="fas fa-tags sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Subscription Catalog</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/data-sets`} activeClassName="active" title="Data Set Management" onClick={onNavigate}>
          <i className="fas fa-database sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Data Set Management</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/maintenance`} activeClassName="active" title="Maintenance" onClick={onNavigate}>
          <i className="fas fa-bullhorn sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Maintenance</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/email-templates`} activeClassName="active" title="Email Templates" onClick={onNavigate}>
          <i className="fas fa-envelope-open-text sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Email Templates</span>
        </NavLink>
      )}
      {showSupportMenu && (
        <NavLink to={`${opsBasePath}/global/ops-audit`} activeClassName="active" title="Audit History" onClick={onNavigate}>
          <i className="fas fa-clipboard-list sidebar-icon" aria-hidden="true"></i>
          <span className="sidebar-label">Audit History</span>
        </NavLink>
      )}
    </nav>
  );

  if (opsAccessDenied) {
    return (
      <div className="auth-page">
        <div className="card auth-card shadow-sm text-center">
          <div className="auth-logo">
            <img src={finbaseLogo} alt="Finbase" />
          </div>
          <h1 className="h4 fw-bold text-dark mb-2">Access Denied</h1>
          <p className="text-muted mb-4">
            You don’t have permission to access this resource.
          </p>
          <a
            className="btn btn-primary"
            href={`${idOrigin}/login?return_to=account&force_account=1&error=access_denied`}
          >
            Back to Sign-In
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="account-shell">
      {isOpsHost && opsMaintenanceStatuses.length > 0 && (
        <div className="alert alert-danger rounded-0 mb-0 py-2 text-center fw-semibold" role="alert">
          Maintenance mode active: {opsMaintenanceSummary}
        </div>
      )}
      {accountData?.org_name && (
        <div className="org-banner">
          <span className="org-banner-label">Organization:</span>
          <span className="org-banner-name">{accountData.org_name}</span>
          <span className="org-banner-user">
            Signed in as {accountData.full_name || accountData.email || 'User'}
          </span>
        </div>
      )}
      {!isOpsHost && <PlatformMessagesBanner surface="account_portal" />}
      <div className={`dashboard${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <div className="mobile-nav-bar">
          <button
            type="button"
            className="btn btn-outline-primary mobile-nav-toggle"
            onClick={() => setMobileNavOpen(true)}
            aria-label={`Open ${isOpsHost ? 'admin' : 'account'} navigation`}
            aria-expanded={mobileNavOpen}
          >
            <i className="fas fa-bars" aria-hidden="true"></i>
            <span>Menu</span>
          </button>
        </div>
        {mobileNavOpen && (
          <div
            className="mobile-nav-backdrop"
            role="presentation"
            onClick={closeMobileNav}
          />
        )}
        <aside
          className={`mobile-nav-drawer${mobileNavOpen ? ' open' : ''}`}
          aria-hidden={!mobileNavOpen}
        >
          <div className="sidebar-header mobile-nav-header">
            <span className="sidebar-title">{isOpsHost ? 'Admin' : 'Account'}</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary sidebar-toggle"
              onClick={closeMobileNav}
              aria-label="Close navigation"
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>
          {renderSidebarNav({ onNavigate: closeMobileNav })}
        </aside>
        <aside className="dashboard-sidebar">
          <div className="sidebar-header">
            <span className="sidebar-title">{isOpsHost ? 'Admin' : 'Account'}</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary sidebar-toggle"
              onClick={toggleSidebar}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <i className={`fas ${sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
            </button>
          </div>
          {renderSidebarNav()}
        </aside>
        <main className="dashboard-main">
          {loading && (
            <div className="dashboard-loading">
              <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          )}
          {!loading && opsAccessDenied && (
            <div className="card">
              <div className="card-body text-center">
                <h3 className="h5 fw-bold mb-2">Access denied</h3>
                <p className="text-muted mb-3">
                  You don’t have permission to access the internal ops portal.
                </p>
                <a
                  className="btn btn-primary"
                  href={`${idOrigin}/login?return_to=account&force_account=1&error=access_denied`}
                >
                  Back to Sign-In
                </a>
              </div>
            </div>
          )}
          {!loading && error && (
            <div className="alert alert-danger" role="alert">{error}</div>
          )}
          {!loading && accountData && !opsAccessDenied && (
            <Switch>
              {isOpsHost ? (
                <>
                  <Route exact path={path}>
                    {showSupportMenu ? <OpsOrgSelect /> : <Redirect to={path} />}
                  </Route>
                  <Route path={`${path}/org/:orgId`}>
                    {showSupportMenu ? <OpsOrgShell /> : <Redirect to={opsBasePath} />}
                  </Route>
                  <Route path={`${path}/global/login-attempts`}>
                    {allowOpsRoutes ? <OpsLoginAttempts /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/auth-throttle`}>
                    {allowOpsRoutes ? <OpsAuthThrottles /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/email-templates`}>
                    {allowOpsRoutes ? <SupportEmailTemplates /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/tax-exemption-requests`}>
                    {allowOpsRoutes ? <OpsTaxExemptionRequests /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/support-portal-identity-cleanup`}>
                    {allowOpsRoutes ? <OpsSupportTicketCleanup /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/subscription-catalog`}>
                    {allowOpsRoutes ? <OpsSubscriptionCatalog /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/data-sets`}>
                    {allowOpsRoutes ? <OpsDataSetManagement /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/maintenance`}>
                    {allowOpsRoutes ? <OpsMaintenance /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/user-metrics`}>
                    <Redirect to={`${opsBasePath}/global/ops-dashboard`} />
                  </Route>
                  <Route path={`${path}/global/ops-dashboard`}>
                    {allowOpsRoutes ? <OpsUserMetrics /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route path={`${path}/global/ops-audit`}>
                    {allowOpsRoutes ? <OpsAuditHistory /> : <Redirect to={`${opsBasePath}/global/email-templates`} />}
                  </Route>
                  <Route exact path={`${path}/global`}>
                    {allowOpsRoutes ? (
                      <Redirect to={`${opsBasePath}/global/email-templates`} />
                    ) : (
                      <Redirect to={opsBasePath} />
                    )}
                  </Route>
                  <Route path={`${path}/support`}>
                    <Redirect to={`${opsBasePath}/global/email-templates`} />
                  </Route>
                  <Route path="*">
                    <Redirect to={opsBasePath} />
                  </Route>
                </>
              ) : (
                <>
                  <Route exact path={path}>
                    <Redirect to={`${url}/overview`} />
                  </Route>
                  <Route path={`${path}/overview`}>
                    <AccountOverview
                      accountData={accountData}
                      orgUsers={orgUsers}
                      apiPermissions={apiPermissions}
                      canManageAccount={canManageAccount}
                    />
                  </Route>
                  <Route path={`${path}/details`}>
                    <AccountDetails accountData={accountData} canManageAccount={canManageAccount} onUpdated={fetchAccount} />
                  </Route>
                  <Route path={`${path}/organization`}>
                    {allowAccountMenus ? (
                      <OrganizationDetails accountData={accountData} canManageAccount={canManageAccount} onUpdated={fetchAccount} />
                    ) : (
                      <Redirect to={`${url}/overview`} />
                    )}
                  </Route>
                  <Route path={`${path}/users`}>
                    {allowAccountMenus && canManageAccount ? (
                      <UserManagement
                        isOrg={isOrg}
                        canManageAccount={canManageAccount}
                        orgUsers={orgUsers}
                        currentUserEmail={currentUserEmail}
                        onUpdated={fetchAccount}
                        canAssignSupport={canAssignSupport}
                        canAssignInternal={canAssignInternal}
                      />
                    ) : (
                      <Redirect to={`${url}/overview`} />
                    )}
                  </Route>
                  <Route path={`${path}/audit`}>
                    {allowAccountMenus && isOrg && canManageAccount ? (
                      <AuditTrail orgUsers={orgUsers} />
                    ) : (
                      <Redirect to={`${url}/overview`} />
                    )}
                  </Route>
                  <Route exact path={`${path}/invites`}>
                    {allowAccountMenus && canManageAccount ? (
                      <Invites isOrg={isOrg} canManageAccount={canManageAccount} />
                    ) : (
                      <Redirect to={`${url}/overview`} />
                    )}
                  </Route>
                  <Route path={`${path}/invites/history`}>
                    {allowAccountMenus && canManageAccount ? (
                      <InviteHistory isOrg={isOrg} canManageAccount={canManageAccount} canAssignSupport={canAssignSupport} />
                    ) : (
                      <Redirect to={`${url}/overview`} />
                    )}
                  </Route>
                  <Route path={`${path}/api-keys/usage`}>
                    {allowAccountMenus && !isSupportOnly ? <ApiKeyUsage /> : <Redirect to={`${url}/overview`} />}
                  </Route>
                  <Route exact path={`${path}/api-keys`}>
                    {allowAccountMenus && !isSupportOnly ? (
                      <ApiKeys canManageKeys={canManageKeys} serviceApiUrl={serviceApiUrl} docsUrl={docsUrl} apiPermissions={apiPermissions} />
                    ) : (
                      <Redirect to={`${url}/overview`} />
                    )}
                  </Route>
                  <Route exact path={`${path}/subscription/checkout`}>
                    {allowAccountMenus && canManageAccount ? <SubscriptionCheckout onUpdated={fetchAccount} /> : <Redirect to={`${url}/overview`} />}
                  </Route>
                  <Route exact path={`${path}/subscription`}>
                    {allowAccountMenus && canManageAccount ? <Subscription onUpdated={fetchAccount} /> : <Redirect to={`${url}/overview`} />}
                  </Route>
                  <Route path={`${path}/email`}>
                    {allowAccountMenus && !isOrg && canManageAccount && !isSupportOnly ? (
                      <EmailUpdate canManageAccount={canManageAccount} onUpdated={fetchAccount} />
                    ) : (
                      <Redirect to={`${url}/overview`} />
                    )}
                  </Route>
                  <Route path={`${path}/support/email-templates`}>
                    {showSupportMenu ? <SupportEmailTemplates /> : <Redirect to={`${url}/overview`} />}
                  </Route>
                  <Route path={`${path}/support`}>
                    {showSupportMenu ? <Support /> : <Redirect to={`${url}/overview`} />}
                  </Route>
                </>
              )}
            </Switch>
          )}
        </main>
      </div>
    </div>
  );
};

export default Account;
